import React, { useState } from "react";
import {
  Box,
  CssBaseline,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Select,
  MenuItem,
  Button,
  IconButton,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
// import axios from "axios";
import api from "../../api/axiosInstance"
import AppBarComponent from "../../components/layout/AppbarComponent";

const functionTypes = ["EI", "EO", "EQ", "ILF", "EIF"];

// Complexity calculation
const calculateComplexity = (func) => {
  const det = parseInt(func.det || 0, 10);
  const ret = parseInt(func.ret || 0, 10);
  const ftr = parseInt(func.ftr || 0, 10);

  switch (func.type) {
    case "EI":
      if (det <= 4 && ftr <= 1) return "Low";
      if (det <= 15 && ftr <= 2) return "Average";
      return "High";
    case "EO":
      if (det <= 5 && ftr <= 1) return "Low";
      if (det <= 15 && ftr <= 3) return "Average";
      return "High";
    case "EQ":
      if (det <= 5 && ftr <= 1) return "Low";
      if (det <= 15 && ftr <= 3) return "Average";
      return "High";
    case "ILF":
      if (ret <= 1 && det <= 19) return "Low";
      if (ret <= 5 && det <= 50) return "Average";
      return "High";
    case "EIF":
      if (ret <= 5 && det <= 19) return "Low";
      if (ret <= 5 && det <= 50) return "Average";
      return "High";
    default:
      return "";
  }
};

// Estimate man-hours
const estimateManHours = (complexity) => {
  switch (complexity) {
    case "Low": return 8;
    case "Average": return 16;
    case "High": return 24;
    default: return 0;
  }
};

const EstimateSoftware = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [functions, setFunctions] = useState([
    { name: "", type: "EI", det: "", ret: "", ftr: "" },
  ]);

  const handleChange = (index, field, value) => {
    const updated = [...functions];
    updated[index][field] = value;
    setFunctions(updated);
  };

  const addFunction = () => {
    setFunctions([...functions, { name: "", type: "EI", det: "", ret: "", ftr: "" }]);
  };

  const removeFunction = (index) => {
    setFunctions(functions.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    try {
      await api.post(`api/software/${id}/functions`, { functions });
      alert("บันทึกข้อมูลเรียบร้อยแล้ว!");
    } catch (err) {
      console.error(err);
      alert("บันทึกไม่สำเร็จ");
    }
  };

  const handleNext = () => {
    navigate(`/software/result/${id}`);
  };

  const totalManHours = functions.reduce((sum, func) => {
    const complexity = calculateComplexity(func);
    return sum + estimateManHours(complexity);
  }, 0);

  return (
    <>
      <CssBaseline />
      <AppBarComponent />
      <Box sx={{ p: 2, minHeight: "100vh", backgroundColor: "#F1F8E9" }}>
        <Paper sx={{ p: 3, borderRadius: 2.5, maxWidth: "95%", margin: "0 auto", backgroundColor: "#F9FBE7" }}>
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
            ประเมิน Function Point - ซอฟต์แวร์ ID: {id}
          </Typography>

          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Function Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>DET</TableCell>
                  <TableCell>RET</TableCell>
                  <TableCell>FTR</TableCell>
                  <TableCell>Complexity</TableCell>
                  <TableCell>Man-Hours</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {functions.map((func, index) => {
                  const complexity = calculateComplexity(func);
                  const manHours = estimateManHours(complexity);
                  return (
                    <TableRow key={index}>
                      <TableCell>
                        <TextField
                          fullWidth size="small"
                          value={func.name}
                          onChange={(e) => handleChange(index, "name", e.target.value)}
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          fullWidth size="small"
                          value={func.type}
                          onChange={(e) => handleChange(index, "type", e.target.value)}
                        >
                          {functionTypes.map((type) => (
                            <MenuItem key={type} value={type}>{type}</MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth size="small" type="number"
                          value={func.det}
                          onChange={(e) => handleChange(index, "det", e.target.value)}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth size="small" type="number"
                          value={func.ret}
                          onChange={(e) => handleChange(index, "ret", e.target.value)}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth size="small" type="number"
                          value={func.ftr}
                          onChange={(e) => handleChange(index, "ftr", e.target.value)}
                        />
                      </TableCell>
                      <TableCell>{complexity}</TableCell>
                      <TableCell>{manHours}</TableCell>
                      <TableCell>
                        <IconButton color="error" onClick={() => removeFunction(index)}>
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow>
                  <TableCell colSpan={6} align="right" sx={{ fontWeight: "bold" }}>
                    รวม Man-Hours
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>{totalManHours}</TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Button variant="outlined" startIcon={<Add />} onClick={addFunction}>
              เพิ่ม Function
            </Button>
            <Box>
              <Button variant="contained" color="primary" onClick={handleSave} sx={{ mr: 1 }}>
                บันทึก
              </Button>
              <Button variant="contained" color="secondary" onClick={handleNext}>
                ถัดไป
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export default EstimateSoftware;
